import { hash } from '@ember/helper';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismBanner } from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { timeout } from 'lumen/utils/waiters';

interface GeneralCopyToClipboardSignature {
  Args: {
    text: string;
  };
}

export default class GeneralCopyToClipboard extends Component<GeneralCopyToClipboardSignature> {
  @service declare intl: Services['intl'];

  @tracked showCopied = false;

  copyToClipboardTask = task(
    { drop: true },
    async (buttonElement): Promise<void> => {
      try {
        // Use writeText for modern browsers.
        await navigator.clipboard.writeText(this.args.text);
      } catch {
        // In case it fails, use execCommand instead (deprecated).
        // https://developer.mozilla.org/en-US/docs/Web/API/Document/execCommand
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const copyToClipboardContent =
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          buttonElement.target.parentElement.querySelector(
            '.copy-to-clipboard__content',
          );
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        copyToClipboardContent.type = 'text';
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        copyToClipboardContent.select();
        document.execCommand('copy');
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        copyToClipboardContent.type = 'hidden';
      } finally {
        this.showCopied = true;
        await timeout('extra-long');
        this.showCopied = false;
      }
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get buttonText() {
    if (this.showCopied) {
      return this.intl.t('general.copy_to_clipboard.success');
    }

    return this.intl.t('general.copy');
  }

  <template>
    <input class="copy-to-clipboard__content" type="hidden" value={{@text}} />
    <PrismBanner
      @bannerMessage={{@text}}
      @hasCta={{true}}
      @isOneLine={{true}}
      @primaryAction={{hash
        action=this.copyToClipboardTask.perform
        iconBefore=(if this.showCopied "checkmark" "copy")
        label=this.buttonText
      }}
      data-test-id="copy-to-clipboard"
    />
  </template>
}
